import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import {
  triggerCanaryWorkflow,
  useInProgressAIVisibilityWorkflow,
  useLatestCompletedAIVisibilityWorkflowDate,
} from '@/api/aiVisibility';
import { useCurrentInstance } from '@/api/organization';
import InfoCircle from '@/assets/svg/bx-info-circle.svg?react';
import { Button } from '@/components/buttons';
import Notification from '@/components/Notification';
import { Page } from '@/components/page';
import { InstanceNavigate } from '@/components/routing';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Text } from '@/components/typography';
import { USDateViewFormat } from '@/constants/formats';
import { CANARY_ERROR_CODES } from '@/error/messages/canary-standard-errors';
import SolAPIError from '@/error/SolAPIError';
import { formatDate } from '@/helper/dateHelper';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

import AIVisibilityCompetitors from './AIVisibilityCompetitors';
import AIVisibilityOverview from './AIVisibilityOverview';
import AIVisibilityPrompts from './AIVisibilityPrompts';
import AIVisibilityRecommendations from './AIVisibilityRecommendations';
import AIVisibilitySettings from './AIVisibilitySettings';

const TAB_ROUTES = {
  overview: ROUTES.aiVisibilityOverview.path,
  prompts: ROUTES.aiVisibilityPrompts.path,
  recommendations: ROUTES.aiVisibilityRecommendations.path,
  competitors: ROUTES.aiVisibilityCompetitors.path,
  settings: ROUTES.aiVisibilitySettings.path,
} as const;

type TabValue = keyof typeof TAB_ROUTES;

const AIVisibility = () => {
  const user = useMe();
  const { instance } = useCurrentInstance();
  const navigate = useInstanceNavigate();
  const { pushNotification, removeNotification } = useNotification();
  const { pathname } = useLocation();
  const canViewSettings = !!user?.isAdmin || !!user?.isC99Superuser;
  const {
    completedAt,
    hasCompletedWorkflow,
    isLoading: isCompletedWorkflowLoading,
    error: completedWorkflowError,
  } = useLatestCompletedAIVisibilityWorkflowDate(instance != null);
  const isInProgressWorkflowEnabled =
    instance != null &&
    !hasCompletedWorkflow &&
    !isCompletedWorkflowLoading &&
    !completedWorkflowError;
  const {
    hasInProgressWorkflow,
    error: inProgressWorkflowError,
    mutate: mutateInProgressWorkflow,
  } = useInProgressAIVisibilityWorkflow(isInProgressWorkflowEnabled);
  const hasWorkflowStatusError =
    completedWorkflowError != null ||
    (isInProgressWorkflowEnabled && inProgressWorkflowError != null);
  const lastUpdatedDate = completedAt
    ? formatDate(completedAt, USDateViewFormat, { isUTC: true })
    : null;
  const [isSettingsDirty, setIsSettingsDirty] = useState(false);
  const [isTriggerPending, setIsTriggerPending] = useState(false);
  const activeTab =
    (Object.entries(TAB_ROUTES) as [TabValue, string][]).find(
      ([, path]) => matchPath(path, pathname) != null,
    )?.[0] ?? 'overview';

  if (instance == null || (isCompletedWorkflowLoading && activeTab !== 'settings')) {
    return null;
  }

  if (!hasCompletedWorkflow && activeTab !== 'settings') {
    return (
      <InstanceNavigate
        to={canViewSettings ? ROUTES.aiVisibilitySettings.path : ROUTES.instance.path}
        replace
      />
    );
  }

  const handleWorkflowStarted = () => {
    if (isInProgressWorkflowEnabled) {
      mutateInProgressWorkflow();
    }
  };

  const handleTabChange = (tabValue: string) => {
    if (activeTab === 'settings' && isSettingsDirty) {
      return;
    }

    const path = TAB_ROUTES[tabValue as TabValue];
    if (path) {
      navigate(path);
    }
  };

  const handleTriggerRefresh = async () => {
    if (!user?.isC99Superuser || isTriggerPending) {
      return;
    }

    setIsTriggerPending(true);
    removeNotification({ type: ['success', 'error'] });
    try {
      const workflow = await triggerCanaryWorkflow();
      if (!workflow?.id) {
        throw new Error('Sol did not return a workflow ID.');
      }

      pushNotification({
        type: 'success',
        message: t`AI Visibility workflow triggered. Workflows typically take 15-30 mins to run once they reach the front of the queue.`,
      });
    } catch (error) {
      const settingsError =
        error instanceof SolAPIError
          ? error.errors[CANARY_ERROR_CODES.PRODUCT_AREA_SETTINGS_REQUIRED]
          : undefined;
      pushNotification({
        type: 'error',
        message:
          settingsError?.message ??
          t`Unable to trigger the AI Visibility workflow. Please try again.`,
      });
    } finally {
      setIsTriggerPending(false);
    }
  };

  return (
    <Page title={t`AI Visibility`} exampleAIQuestions={[]}>
      {hasWorkflowStatusError && (
        <div role="alert">
          <Notification type="error" size="large" className="text-gray-900">
            <Trans>
              Unable to check AI Visibility analysis status. We’ll keep trying automatically.
            </Trans>
          </Notification>
        </div>
      )}
      {!hasCompletedWorkflow && !hasWorkflowStatusError && hasInProgressWorkflow && (
        <div role="status">
          <Notification type="info" size="large" className="text-gray-900">
            <Trans>
              Your first AI Visibility analysis is in progress. Results typically take 30–60 minutes
              once your analysis reaches the front of the queue. You'll receive an email when it's
              complete.
            </Trans>
          </Notification>
        </div>
      )}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList variant="line" aria-label={t`AI Visibility sections`}>
            {hasCompletedWorkflow && (
              <>
                <TabsTrigger value="overview">
                  <Trans>Overview</Trans>
                </TabsTrigger>
                <TabsTrigger value="prompts">
                  <Trans>Prompts</Trans>
                </TabsTrigger>
                <TabsTrigger value="recommendations">
                  <Trans>Recommendations</Trans>
                </TabsTrigger>
                <TabsTrigger value="competitors">
                  <Trans>Competitors</Trans>
                </TabsTrigger>
              </>
            )}
            {canViewSettings && (
              <TabsTrigger value="settings">
                <Trans>Settings</Trans>
              </TabsTrigger>
            )}
          </TabsList>
          {hasCompletedWorkflow && (
            <div className="ml-auto flex items-center gap-2">
              {lastUpdatedDate && (
                <span className="inline-flex items-center gap-1">
                  <Text variant="body2">
                    <Trans>Last Updated: {lastUpdatedDate}</Trans>
                  </Text>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          className="-my-0.5 text-aqua-400!"
                          size="icon-small"
                          aria-label={t`Refresh schedule`}
                        >
                          <InfoCircle />
                        </Button>
                      }
                    />
                    <TooltipContent side="bottom">
                      <Trans>Refreshes happen on Saturday at midnight UTC</Trans>
                    </TooltipContent>
                  </Tooltip>
                </span>
              )}
              {user?.isC99Superuser && (
                <Button isLoading={isTriggerPending} onClick={handleTriggerRefresh}>
                  <Trans>Trigger Refresh</Trans>
                </Button>
              )}
            </div>
          )}
        </div>
        <TabsContent value="overview">
          <AIVisibilityOverview />
        </TabsContent>
        <TabsContent value="prompts">
          <AIVisibilityPrompts />
        </TabsContent>
        <TabsContent value="recommendations">
          <AIVisibilityRecommendations />
        </TabsContent>
        <TabsContent value="competitors">
          <AIVisibilityCompetitors />
        </TabsContent>
        {canViewSettings && (
          <TabsContent value="settings">
            <AIVisibilitySettings
              onDirtyChange={setIsSettingsDirty}
              onWorkflowStarted={handleWorkflowStarted}
            />
          </TabsContent>
        )}
      </Tabs>
    </Page>
  );
};

export default AIVisibility;
